import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Project } from '../types'

export default function InviteClientModal({
  project,
  onClose,
  onUpdated,
}: {
  project: Project
  onClose: () => void
  onUpdated: (p: Project) => void
}) {
  const [email, setEmail] = useState('')
  const [adding, setAdding] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [gmailReady, setGmailReady] = useState(false)
  const [sending, setSending] = useState(false)
  const [sentMsg, setSentMsg] = useState<string | null>(null)

  useEffect(() => {
    void api.integrations.status().then((s) => setGmailReady(s.gmail.connected))
  }, [])

  const list: string[] = (() => {
    try {
      return project.client_emails ? (JSON.parse(project.client_emails) as string[]) : []
    } catch {
      return []
    }
  })()

  const inviteText = email.trim()
    ? `Hi —

I've set you up with read-only access to the Heritage Command Center for ${project.name}. You'll be able to see build progress, photos, selections, and any change orders.

Sign in here: https://hq.heritagehomesmn.com

When you visit, Cloudflare will email a one-time code to ${email.trim()} — paste that in and you're in. No password to remember.

Let me know if you have any trouble getting in.

— Dustin`
    : ''

  async function add() {
    if (!email.trim()) return
    setAdding(true)
    setErr(null)
    try {
      const updated = await api.projects.addClientEmail(project.id, email)
      onUpdated(updated)
      // Don't close — show the invite text so user can copy it
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setAdding(false)
    }
  }

  async function remove(addr: string) {
    if (!window.confirm(`Remove ${addr}? They'll lose access immediately.`)) return
    const updated = await api.projects.removeClientEmail(project.id, addr)
    onUpdated(updated)
  }

  function copyInvite() {
    void navigator.clipboard.writeText(inviteText)
  }

  async function sendInvite() {
    if (!inviteText || !email.trim()) return
    setSending(true)
    setErr(null)
    setSentMsg(null)
    try {
      await api.integrations.gmailSend({
        to: email.trim(),
        subject: `Access to Heritage Command Center for ${project.name}`,
        body: inviteText,
      })
      setSentMsg(`Invitation emailed to ${email.trim()}`)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setSending(false)
    }
  }

  const justAdded = list.includes(email.trim().toLowerCase())

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Invite client to {project.name}</h3>
        <p className="muted small mb-3">
          Add an email to the project's client list. They get a read-only portal view: progress,
          selections, change orders, photos. They don't see your sub bids, GC fee, daily logs, or
          internal punch list.
        </p>

        <label className="form-row">
          <span>Email address</span>
          <input
            autoFocus
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="keith@example.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add()
            }}
          />
        </label>

        {err && <div className="card alert mb-3">{err}</div>}

        <div className="row gap-2 mb-3">
          <button className="btn-primary" onClick={add} disabled={adding || !email.trim()}>
            {adding ? 'Adding…' : justAdded ? '✓ Added' : 'Add to project'}
          </button>
          <button className="btn-ghost" onClick={onClose}>
            Done
          </button>
        </div>

        {justAdded && inviteText && (
          <div className="card mb-3">
            <div className="row-between mb-2">
              <h4 className="muted small" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Copy this and email it to them
              </h4>
              <div className="row gap-2">
                <button className="btn-primary small" onClick={copyInvite}>
                  Copy
                </button>
                {gmailReady && (
                  <button className="btn-primary small" onClick={sendInvite} disabled={sending}>
                    {sending ? 'Sending…' : 'Send via Gmail'}
                  </button>
                )}
              </div>
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13 }}>
              {inviteText}
            </pre>
            {sentMsg && (
              <div className="callout small mt-2" style={{ borderLeftColor: 'var(--ok)' }}>
                {sentMsg}
              </div>
            )}
          </div>
        )}

        {list.length > 0 && (
          <div className="card no-pad">
            <div className="row-list" style={{ cursor: 'default' }}>
              <h4 className="muted small grow" style={{ margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Currently invited ({list.length})
              </h4>
            </div>
            {list.map((addr) => (
              <div key={addr} className="row-list" style={{ cursor: 'default' }}>
                <div className="grow">
                  <code>{addr}</code>
                </div>
                <button className="btn-ghost small" onClick={() => remove(addr)}>
                  ✕ Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
